import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react'
import { authApi, type UserProfile, type RegisterData } from '@/lib/authApi'

interface AuthState {
  user: UserProfile | null
  loading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  register: (data: RegisterData) => Promise<void>
  logout: () => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthState | null>(null)

// sessionStorage cache of the user profile so warm-tab navigations render
// instantly while we revalidate /api/auth/me in the background. The httpOnly
// JWT cookie remains the source of truth - this cache is UI-only.
const CACHE_KEY = 'leviathan_auth_user_v1'

function readCachedUser(): UserProfile | null {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY)
    return raw ? (JSON.parse(raw) as UserProfile) : null
  } catch {
    return null
  }
}

function writeCachedUser(u: UserProfile | null): void {
  try {
    if (u) sessionStorage.setItem(CACHE_KEY, JSON.stringify(u))
    else sessionStorage.removeItem(CACHE_KEY)
  } catch {
    // sessionStorage can throw in privacy mode; ignore.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const cached = readCachedUser()
  const [user, setUser] = useState<UserProfile | null>(cached)
  // Optimistic: if we have a cached user, render immediately and revalidate
  // in the background. Without a cache we still gate on the network probe.
  const [loading, setLoading] = useState(cached === null)

  // On mount, restore session from the leviathan_auth httpOnly cookie by
  // probing /api/auth/me. There is no client-side token to inspect - the
  // server is the source of truth (Phase 4 SEC-06 / D-05).
  useEffect(() => {
    authApi
      .me()
      .then((u) => {
        setUser(u)
        writeCachedUser(u)
      })
      .catch(() => {
        setUser(null)
        writeCachedUser(null)
      })
      .finally(() => setLoading(false))
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const resp = await authApi.login(email, password)
    setUser(resp.user)
    writeCachedUser(resp.user)
  }, [])

  const register = useCallback(async (data: RegisterData) => {
    const resp = await authApi.register(data)
    setUser(resp.user)
    writeCachedUser(resp.user)
  }, [])

  const logout = useCallback(async () => {
    await authApi.logout()
    setUser(null)
    writeCachedUser(null)
  }, [])

  const refreshUser = useCallback(async () => {
    const profile = await authApi.me()
    setUser(profile)
    writeCachedUser(profile)
  }, [])

  return (
    <AuthContext.Provider
      value={{ user, loading, isAuthenticated: !!user, login, register, logout, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}
